/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { canManageActions, canUpdateAction } from '../permissions'

const STATUS_CONFIG = {
  open:        { label: 'Open',        colour: '4A5568', bg: 'F7FAFC' },
  in_progress: { label: 'In Progress', colour: 'C9A84C', bg: 'FFF8E7' },
  complete:    { label: 'Complete',    colour: '2D6A4F', bg: 'EEF7F2' },
  cancelled:   { label: 'Cancelled',  colour: '718096', bg: 'F7FAFC' },
}

function getDaysUntil(date) {
  if (!date) return null
  return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24))
}

function formatDate(date) {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  })
}

function getDueDateColour(date, status) {
  if (status === 'complete' || status === 'cancelled') return 'var(--slate)'
  const days = getDaysUntil(date)
  if (days === null) return 'var(--slate)'
  if (days < 0) return 'var(--red)'
  if (days <= 7) return 'var(--gold-dark)'
  return 'var(--slate)'
}

function ActionTracker({ opportunity, profile, orgUsers, onClose }) {
  const [actions, setActions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingAction, setEditingAction] = useState(null)
  const [error, setError] = useState(null)

  const [form, setForm] = useState({
    description: '',
    due_date: '',
    assigned_to: '',
    assigned_to_name: '',
    comments: ''
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadActions()
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const loadActions = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('actions')
      .select('*')
      .eq('opportunity_id', opportunity.id)
      .order('action_number')
    setActions(data || [])
    setLoading(false)
  }

  const resetForm = () => {
    setForm({ description: '', due_date: '', assigned_to: '', comments: '' })
    setShowAdd(false)
    setEditingAction(null)
    setError(null)
  }

  const handleSave = async () => {
    if (!form.description.trim()) return
    setError(null)

    if (editingAction) {
      const { error } = await supabase
        .from('actions')
        .update({
          description: form.description.trim(),
          due_date: form.due_date || null,
          assigned_to: form.assigned_to === 'other' ? null : form.assigned_to || null,
          assigned_to_name: form.assigned_to === 'other' ? form.assigned_to_name.trim() || null : null,
          comments: form.comments || null
        })
        .eq('id', editingAction.id)

      if (error) { setError(error.message); return }
      setActions(prev => prev.map(a =>
        a.id === editingAction.id
          ? { ...a, description: form.description.trim(), due_date: form.due_date || null, assigned_to: form.assigned_to === 'other' ? null : form.assigned_to || null, assigned_to_name: form.assigned_to === 'other' ? form.assigned_to_name.trim() || null : null, comments: form.comments || null }
          : a
      ))
    } else {
      const { data, error } = await supabase
        .from('actions')
        .insert([{
          opportunity_id: opportunity.id,
          organisation_id: profile.organisation_id,
          description: form.description.trim(),
          due_date: form.due_date || null,
          assigned_to: form.assigned_to === 'other' ? null : form.assigned_to || null,
          assigned_to_name: form.assigned_to === 'other' ? form.assigned_to_name.trim() || null : null,
          comments: form.comments || null,
          status: 'open',
          created_by: profile.id
        }])
        .select()
        .single()

      if (error) { setError(error.message); return }

      // Send notification if assigned to someone else
      if (form.assigned_to && form.assigned_to !== 'other' && form.assigned_to !== profile.id) {
        await supabase
          .from('notifications')
          .insert([{
            user_id: form.assigned_to,
            organisation_id: profile.organisation_id,
            opportunity_id: opportunity.id,
            type: 'action_assigned',
            message: `You have been assigned an action on ${opportunity.title}: ${form.description.trim()}`,
            read: false
          }])
      }

      setActions(prev => [...prev, data])
    }

    resetForm()
  }

  const handleStatusChange = async (actionId, newStatus) => {
    await supabase
      .from('actions')
      .update({ status: newStatus })
      .eq('id', actionId)
    setActions(prev => prev.map(a =>
      a.id === actionId ? { ...a, status: newStatus } : a
    ))
  }

  const handleDelete = async (actionId) => {
    await supabase.from('actions').delete().eq('id', actionId)
    setActions(prev => prev.filter(a => a.id !== actionId))
  }

  const handleEdit = (action) => {
    setForm({
      description: action.description || '',
      due_date: action.due_date ? new Date(action.due_date).toISOString().split('T')[0] : '',
      assigned_to: action.assigned_to_name ? 'other' : (action.assigned_to || ''),
      assigned_to_name: action.assigned_to_name || '',
      comments: action.comments || ''
    })
    setEditingAction(action)
    setShowAdd(true)
  }

  const openCount = actions.filter(a => a.status === 'open' || a.status === 'in_progress').length
  const overdueCount = actions.filter(a => {
    if (a.status === 'complete' || a.status === 'cancelled') return false
    return getDaysUntil(a.due_date) !== null && getDaysUntil(a.due_date) < 0
  }).length

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>

      {/* Header */}
      <div style={{
        background: 'var(--green-dark)', padding: '20px 24px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{
              fontSize: '0.6875rem', fontWeight: 600,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'var(--green-light)', marginBottom: '6px'
            }}>
              Action Tracker
            </div>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: '1.5rem', fontWeight: 600, color: 'var(--white)'
            }}>
              {opportunity.title}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>
              {opportunity.buyer}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            {overdueCount > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: '2rem', fontWeight: 700,
                  color: 'var(--red)', lineHeight: 1
                }}>
                  {overdueCount}
                </div>
                <div style={{
                  fontSize: '0.625rem', fontWeight: 600,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: 'var(--red)'
                }}>
                  Overdue
                </div>
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '2rem', fontWeight: 700,
                color: 'var(--white)', lineHeight: 1
              }}>
                {openCount}
              </div>
              <div style={{
                fontSize: '0.625rem', fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--green-light)'
              }}>
                Open
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Add / Edit form */}
      {showAdd && (
        <div style={{
          background: 'var(--white)', border: '1px solid var(--green-light)',
          padding: '24px', marginBottom: '24px',
          borderLeft: '4px solid var(--green)'
        }}>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: '1.125rem', fontWeight: 600,
            color: 'var(--green-dark)', marginBottom: '16px'
          }}>
            {editingAction ? `Edit Action #${editingAction.action_number}` : 'New Action'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Description *</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="What needs to be done?"
                rows={2}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                value={form.assigned_to}
                onChange={e => setForm({ ...form, assigned_to: e.target.value, assigned_to_name: '' })}
                style={{ width: '100%' }}
              >
                <option value="">Unassigned</option>
                {orgUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.username}
                  </option>
                ))}
                <option value="other">Other (external contact)</option>
              </select>
            </div>
            {form.assigned_to === 'other' && (
              <div className="form-group">
                <label>External Contact Name</label>
                <input
                  type="text"
                  value={form.assigned_to_name}
                  onChange={e => setForm({ ...form, assigned_to_name: e.target.value })}
                  placeholder="e.g. Jane Smith (Client), John Brown (Legal)"
                  autoFocus
                />
              </div>
            )}
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Comments (optional)</label>
              <textarea
                value={form.comments}
                onChange={e => setForm({ ...form, comments: e.target.value })}
                placeholder="Any additional context or notes"
                rows={2}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn--primary"
              onClick={handleSave}
              disabled={!form.description.trim()}
            >
              {editingAction ? 'Save Changes' : 'Add Action'}
            </button>
            <button className="btn btn--secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Actions list */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '16px'
        }}>
          <div style={{
            fontSize: '0.75rem', fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'var(--green-dark)'
          }}>
            {actions.length} Action{actions.length !== 1 ? 's' : ''}
          </div>
          {!showAdd && (
            <button
              className="btn btn--primary"
              style={{ padding: '6px 16px', fontSize: '0.8125rem' }}
              onClick={() => setShowAdd(true)}
            >
              + Add Action
            </button>
          )}
        </div>

        {loading && <p style={{ color: 'var(--slate)' }}>Loading actions...</p>}

        {!loading && actions.length === 0 && (
          <div className="card">
            <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '24px 0' }}>
              No actions yet. Click <strong>+ Add Action</strong> to create the first one.
            </p>
          </div>
        )}

        {actions.map(action => {
          const statusConfig = STATUS_CONFIG[action.status]
          const daysUntil = getDaysUntil(action.due_date)
          const isOverdue = action.due_date &&
            daysUntil < 0 &&
            action.status !== 'complete' &&
            action.status !== 'cancelled'
          const isDueSoon = action.due_date &&
            daysUntil !== null &&
            daysUntil >= 0 &&
            daysUntil <= 7 &&
            action.status !== 'complete' &&
            action.status !== 'cancelled'
          const assignedUser = orgUsers.find(u => u.id === action.assigned_to)

          return (
            <div
              key={action.id}
              style={{
                background: 'var(--white)',
                border: `1px solid ${isOverdue ? 'var(--red)' : isDueSoon ? 'var(--gold)' : 'var(--green-light)'}`,
                marginBottom: '8px',
                borderLeft: `4px solid ${isOverdue ? 'var(--red)' : isDueSoon ? 'var(--gold)' : 'var(--green-light)'}`,
                transition: 'box-shadow 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(45,106,79,0.08)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ padding: '16px 20px' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', gap: '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: '10px', marginBottom: '6px', flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700,
                        color: 'var(--slate)', background: 'var(--off-white)',
                        padding: '2px 8px', border: '1px solid var(--silver)'
                      }}>
                        #{action.action_number}
                      </span>
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        color: `#${statusConfig.colour}`,
                        background: `#${statusConfig.bg}`,
                        padding: '2px 8px'
                      }}>
                        {statusConfig.label}
                      </span>
                      {isOverdue && (
                        <span style={{
                          fontSize: '0.625rem', fontWeight: 700,
                          color: 'var(--white)', background: 'var(--red)',
                          padding: '2px 8px'
                        }}>
                          OVERDUE
                        </span>
                      )}
                      {isDueSoon && !isOverdue && (
                        <span style={{
                          fontSize: '0.625rem', fontWeight: 700,
                          color: 'var(--white)', background: 'var(--gold-dark)',
                          padding: '2px 8px'
                        }}>
                          DUE SOON
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: '0.9375rem', color: 'var(--charcoal)',
                      fontWeight: 500, marginBottom: '8px', lineHeight: 1.4
                    }}>
                      {action.description}
                    </div>
                    {action.comments && (
                      <div style={{
                        fontSize: '0.8125rem', color: 'var(--slate)',
                        fontStyle: 'italic', marginBottom: '8px',
                        lineHeight: 1.4
                      }}>
                        {action.comments}
                      </div>
                    )}
                    <div style={{
                      display: 'flex', gap: '20px',
                      flexWrap: 'wrap', fontSize: '0.8125rem'
                    }}>
                      {action.due_date && (
                        <div>
                          <span style={{ color: 'var(--green)', fontWeight: 600, marginRight: '4px' }}>
                            Due:
                          </span>
                          <span style={{
                            color: getDueDateColour(action.due_date, action.status),
                            fontWeight: isOverdue || isDueSoon ? 600 : 400
                          }}>
                            {formatDate(action.due_date)}
                            {daysUntil !== null && action.status !== 'complete' && action.status !== 'cancelled' && (
                              <span style={{ marginLeft: '6px' }}>
                                {daysUntil < 0
                                  ? `(${Math.abs(daysUntil)} days overdue)`
                                  : daysUntil === 0
                                  ? '(due today)'
                                  : `(${daysUntil} days)`}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      {(assignedUser || action.assigned_to_name) && (
                      <div>
                        <span style={{ color: 'var(--green)', fontWeight: 600, marginRight: '4px' }}>
                          Assigned:
                        </span>
                        {assignedUser ? (
                          <span style={{ color: 'var(--charcoal)' }}>
                            {assignedUser.full_name || assignedUser.username}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--charcoal)', fontStyle: 'italic' }}>
                            {action.assigned_to_name} (external)
                          </span>
                        )}
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Status selector */}
                  <div style={{ flexShrink: 0 }}>
                    <select
                      value={action.status}
                      onChange={e => handleStatusChange(action.id, e.target.value)}
                      style={{
                        padding: '5px 8px',
                        border: `1px solid #${statusConfig.colour}`,
                        fontSize: '0.75rem', fontWeight: 600,
                        color: `#${statusConfig.colour}`,
                        background: `#${statusConfig.bg}`,
                        borderRadius: 0, outline: 'none', cursor: 'pointer'
                      }}
                    >
                      {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div style={{
                borderTop: '1px solid var(--green-light)',
                background: 'var(--off-white)',
                padding: '6px 16px',
                display: 'flex', gap: '8px', alignItems: 'center'
              }}>
                <button
                  onClick={() => canUpdateAction(profile, action) && handleEdit(action)}
                  disabled={!canUpdateAction(profile, action)}
                  style={{
                    background: 'none', border: '1px solid var(--green)',
                    color: 'var(--green)', padding: '3px 12px',
                    fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => canManageActions(profile) && handleDelete(action.id)}
                  disabled={!canManageActions(profile)}
                  style={{
                    background: 'none', border: '1px solid var(--silver)',
                    color: 'var(--slate)', padding: '3px 10px',
                    fontSize: '0.75rem', cursor: 'pointer',
                    marginLeft: 'auto'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <div className="actions">
        <button className="btn btn--secondary" onClick={onClose}>
          ← Back to Pipeline
        </button>
      </div>
    </div>
  )
}

export default ActionTracker